import client from "../client";
import type {
  SilenceCause,
  ConditionReasonCategory,
  ConditionScopeType,
  DiagnoseRunOutcome,
  EnginesPayload,
  FixStepAction,
  FleetMetricsPayload,
  FleetSummary,
  HistoryLog,
  HotelDetail,
  HotelListFilters,
  HotelListRow,
  IncidentDetail,
  IncidentStatus,
  LockRemovalPreview,
  OnboardingHiddenRow,
  OnboardingHiddenRowsPayload,
  OverrideState,
  Page,
  QueuesPayload,
  RuleRow,
  RunRecord,
  TrackingAccessMode,
  TrackingLockKind,
  TrackingSettingsPayload,
  UpdateTrackingSettingsBody,
  WorklistFilters,
  WorklistItem,
} from "../../pages/tracking/health/types";

/**
 * Tracking Health API — both portal-facing surfaces share one mount:
 * `GET/POST /admin/tracking-health/...` (admin JWT via the shared client).
 *
 * The operator surface (`/tracking-tracker/*`, x-api-key) is deliberately NOT
 * here — the portal holds no service key and must not proxy one.
 */

/** The write/read surfaces normalize `:customerId` digits-only — strip
 *  dashes (and anything else) before building URLs. */
function cid(customerId: string): string {
  return (customerId || "").replace(/\D/g, "");
}

const BASE = "/admin/tracking-health";

/** Diagnose-now (and fix-log with verify) run a full five-layer Validator
 *  run synchronously — measured 14–84s. Allow well past that; a timeout is a
 *  network timeout, not a failed run (spec §8). */
const LONG_RUN_TIMEOUT_MS = 180_000;

function listParams(filters: WorklistFilters | HotelListFilters) {
  const params: Record<string, string | number> = {};
  if (filters.state) params.state = filters.state;
  if (filters.engine) params.engine = filters.engine;
  if (filters.incidentStatus) params.incidentStatus = filters.incidentStatus;
  if (filters.q) params.q = filters.q;
  if (filters.page) params.page = filters.page;
  if (filters.limit) params.limit = filters.limit;
  return params;
}

export const trackingHealth = {
  // ─── Reads (12 GETs) ───────────────────────────────────────────────────────

  summary: (): Promise<FleetSummary> =>
    client.get(`${BASE}/summary`).then((r) => r.data),

  /** The fleet table's metric columns — joined to `hotels` by customerId;
   *  the table degrades to em-dashes when this read is unavailable. */
  fleetMetrics: (): Promise<FleetMetricsPayload> =>
    client.get(`${BASE}/fleet-metrics`).then((r) => r.data),

  worklist: (filters: WorklistFilters): Promise<Page<WorklistItem>> =>
    client
      .get(`${BASE}/worklist`, { params: listParams(filters) })
      .then((r) => r.data),

  hotels: (filters: HotelListFilters): Promise<Page<HotelListRow>> =>
    client
      .get(`${BASE}/hotels`, { params: listParams(filters) })
      .then((r) => r.data),

  hotelDetail: (customerId: string): Promise<HotelDetail> =>
    client.get(`${BASE}/hotels/${cid(customerId)}`).then((r) => r.data),

  ruleEvaluations: (
    customerId: string,
    opts: { rule?: string; limit?: number } = {},
  ): Promise<RuleRow[]> =>
    client
      .get(`${BASE}/hotels/${cid(customerId)}/rule-evaluations`, {
        params: opts,
      })
      .then((r) => r.data),

  lockRemovalPreview: (
    customerId: string,
    lock: TrackingLockKind,
  ): Promise<LockRemovalPreview> =>
    client
      .get(`${BASE}/hotels/${cid(customerId)}/locks/${lock}/removal-preview`)
      .then((r) => r.data),

  incident: (incidentId: string): Promise<IncidentDetail> =>
    client.get(`${BASE}/incidents/${incidentId}`).then((r) => r.data),

  run: (runId: string): Promise<RunRecord> =>
    client.get(`${BASE}/runs/${runId}`).then((r) => r.data),

  engines: (): Promise<EnginesPayload> =>
    client.get(`${BASE}/engines`).then((r) => r.data),


  queues: (): Promise<QueuesPayload> =>
    client.get(`${BASE}/queues`).then((r) => r.data),

  /** The "Still onboarding" rows an operator has judged stuck or churned —
   *  fleet-wide and durable (it was this browser's localStorage until
   *  2026-08-17), so a row triaged on one machine stays triaged on the next. */
  onboardingHiddenRows: (): Promise<OnboardingHiddenRowsPayload> =>
    client.get(`${BASE}/onboarding/hidden-rows`).then((r) => r.data),

  /** The fleet-wide settings (backend ADR 0039): the low-volume threshold and
   *  window, and — Advanced — the click floor and the allowance's backstop,
   *  with the code defaults and who last changed them. */
  settings: (): Promise<TrackingSettingsPayload> =>
    client.get(`${BASE}/settings`).then((r) => r.data),

  // ─── Mutations (hotel-scoped; the backend stamps `admin:<email>`) ─────────

  /** Returns the run OUTCOME (runId + outcome counts) — the cells live on
   *  `GET runs/:runId`, fetched with the returned id. */
  diagnoseNow: (
    customerId: string,
    body: { dryRun?: boolean } = {},
  ): Promise<DiagnoseRunOutcome> =>
    client
      .post(`${BASE}/${cid(customerId)}/diagnose-now`, body, {
        timeout: LONG_RUN_TIMEOUT_MS,
      })
      .then((r) => r.data),

  /** `problem` scopes the note to one entry on the active fix plan — a label
   *  for the reader, never an authority: the incident keeps its single
   *  blocked-on note and its one reminder clock. Omit it for a note about the
   *  incident as a whole. */
  logNote: (customerId: string, body: { note: string; problem?: string }) =>
    client
      .post(`${BASE}/${cid(customerId)}/incident/note`, body)
      .then((r) => r.data),

  /**
   * Report a fix against one problem. The server resolves that entry and, once
   * the last one closes, reaches fix-applied and runs its own cheap
   * corroboration pass.
   *
   * The endpoint still accepts `verify` (asking for the FULL five-layer run
   * instead of the cheap one), and this client deliberately never sends it —
   * see the note in `LogFixModal`.
   */
  fixLog: (customerId: string, body: { problem: string; note: string }) =>
    client
      .post(`${BASE}/${cid(customerId)}/incident/fix-log`, body)
      .then((r) => r.data),

  /**
   * Correct the wording of one `fix-logged` or `note` entry — the only two
   * events a human composed (every other row's reason is the machine narrating
   * what it did, and the server refuses those with a 400).
   *
   * It edits words and nothing else: no run fires, no problem resolves, and
   * the 7-day reminder clock does NOT move — a typo must not buy another week
   * of quiet. The superseded text is kept server-side in the admin log.
   */
  editIncidentNote: (
    customerId: string,
    eventId: string,
    body: { note: string },
  ) =>
    client
      .post(`${BASE}/${cid(customerId)}/incident/events/${eventId}/note`, body)
      .then((r) => r.data),

  /**
   * Hide one row from a history a human reads. A REDACTION, not a delete: the
   * row is stamped who/when/why, vanishes from these reads, and stays visible
   * to every machine derivation — so it changes no state.
   *
   * Consequently, removing a `silenced` row does not end a silence (the server
   * refuses with 409 until you have cleared it), removing a lock event does not
   * lift the lock, and removing a state change does not restore a state.
   * `reason` is mandatory.
   */
  removeHistoryRow: (
    customerId: string,
    log: HistoryLog,
    rowId: string,
    body: { reason: string },
  ) =>
    client
      .post(`${BASE}/${cid(customerId)}/history/${log}/${rowId}/remove`, body)
      .then((r) => r.data),

  /**
   * Move the open incident between statuses. Both CLOSED statuses are refused
   * by the server (400): `verified` is the proof booking's and `closed_left_fleet`
   * is the fleet sync's. There is no human close — the false-positive close was
   * retired in favour of `silenceIncident` below.
   */
  transition: (
    customerId: string,
    body: {
      to: IncidentStatus;
      reason: string;
      note?: string;
    },
  ) =>
    client
      .post(`${BASE}/${cid(customerId)}/incident/transition`, body)
      .then((r) => r.data),

  /**
   * Defer the open incident (ADR 0017) — the disposition that replaced the
   * false-positive close. `days` defaults to 10 server-side; `note` and `cause`
   * are optional and there is deliberately NO reason field — do not add one.
   *
   * Silencing changes nothing else: the incident stays open, the hotel stays
   * Unhealthy, and its worklist row keeps its group, position and sort key.
   * It makes that incident quiet until the date, with one exception the server
   * owns — a Rule 2 signal still alerts, and does not end the silence.
   *
   * `cause` is the codification counter's only input. 400 if it is outside the
   * picklist, or if it is `other` without a `note`.
   */
  silenceIncident: (
    customerId: string,
    body: { days?: number; note?: string; cause?: SilenceCause } = {},
  ) =>
    client
      .post(`${BASE}/${cid(customerId)}/incident/silence`, body)
      .then((r) => r.data),

  /** End a standing silence early — 409 when none is standing (an expired
   *  one included: it is already back on the ordinary cadence). */
  clearSilence: (customerId: string, body: { note?: string } = {}) =>
    client
      .post(`${BASE}/${cid(customerId)}/incident/silence/clear`, body)
      .then((r) => r.data),

  refuteHypothesis: (
    customerId: string,
    body: { hypothesis: string; key?: string; note?: string },
  ) =>
    client
      .post(`${BASE}/${cid(customerId)}/incident/hypotheses/refute`, body)
      .then((r) => r.data),

  investigate: (customerId: string) =>
    client
      .post(`${BASE}/${cid(customerId)}/investigate`, {}, {
        timeout: LONG_RUN_TIMEOUT_MS,
      })
      .then((r) => r.data),

  addCondition: (
    customerId: string,
    body: {
      scopeType: ConditionScopeType;
      scopeId: string;
      reasonCategory: ConditionReasonCategory;
      reason: string;
      exitCondition?: string;
    },
  ) =>
    client
      .post(`${BASE}/${cid(customerId)}/conditions`, body)
      .then((r) => r.data),

  removeCondition: (
    customerId: string,
    conditionId: string,
    body: { reason: string },
  ) =>
    client
      .post(`${BASE}/${cid(customerId)}/conditions/${conditionId}/remove`, body)
      .then((r) => r.data),

  confirmCondition: (
    customerId: string,
    conditionId: string,
    body: { note?: string } = {},
  ) =>
    client
      .post(
        `${BASE}/${cid(customerId)}/conditions/${conditionId}/confirm`,
        body,
      )
      .then((r) => r.data),

  reconfirmDispositions: (customerId: string, body: { note?: string } = {}) =>
    client
      .post(`${BASE}/${cid(customerId)}/reconfirm-dispositions`, body)
      .then((r) => r.data),

  applyLock: (
    customerId: string,
    lock: TrackingLockKind,
    body: { reason: string },
  ) =>
    client
      .post(`${BASE}/${cid(customerId)}/locks/${lock}/apply`, body)
      .then((r) => r.data),

  removeLock: (
    customerId: string,
    lock: TrackingLockKind,
    body: { reason: string },
  ): Promise<{ tdsdReset: boolean; skippedRuns: number | null }> =>
    client
      .post(`${BASE}/${cid(customerId)}/locks/${lock}/remove`, body)
      .then((r) => r.data),

  applyOverride: (
    customerId: string,
    body: { state: OverrideState; reason: string; note: string },
  ) =>
    client
      .post(`${BASE}/${cid(customerId)}/override`, body)
      .then((r) => r.data),

  clearOverride: (customerId: string, body: { reason: string }) =>
    client
      .post(`${BASE}/${cid(customerId)}/override/clear`, body)
      .then((r) => r.data),

  setProofRequirement: (
    customerId: string,
    body: { count: number | null; reason: string },
  ) =>
    client
      .post(`${BASE}/${cid(customerId)}/proof-requirement`, body)
      .then((r) => r.data),

  /** Declare what access Autumn holds to this hotel's GTM container and GA4
   *  property. `ui-only` requires the note — the backend refuses without it. */
  setAccessMode: (
    customerId: string,
    body: { mode: TrackingAccessMode; note?: string; reason: string },
  ) =>
    client
      .post(`${BASE}/${cid(customerId)}/access-mode`, body)
      .then((r) => r.data),

  planStepAction: (
    customerId: string,
    stepIndex: number,
    action: FixStepAction,
    body: { note?: string } = {},
  ) =>
    client
      .post(
        `${BASE}/${cid(customerId)}/plan/steps/${stepIndex}/${action}`,
        body,
      )
      .then((r) => r.data),

  // ─── Mutations (onboarding-section row visibility) ────────────────────────
  // The row key is a platform userId as often as a customer id, so it is NOT
  // digit-normalized — it goes up exactly as the merged table computed it.

  /** Hide one onboarding row: stuck indefinitely / churned mid-onboarding.
   *  Pass the joined `customerId` when the row has an enrolled hotel, so the
   *  judgement survives the row's key changing spelling later. Idempotent —
   *  a re-hide keeps the first judgement's stamps. */
  hideOnboardingRow: (
    rowKey: string,
    body: { customerId?: string | null; reason?: string } = {},
  ): Promise<OnboardingHiddenRow> =>
    client
      .post(
        `${BASE}/onboarding/hidden-rows/${encodeURIComponent(rowKey)}`,
        body,
      )
      .then((r) => r.data),

  /** Put the row back in the census. The server keeps the record (who hid it,
   *  who restored it); idempotent when nothing was hidden. */
  unhideOnboardingRow: (
    rowKey: string,
    body: { reason?: string } = {},
  ): Promise<{ rowKey: string; hidden: false }> =>
    client
      .post(
        `${BASE}/onboarding/hidden-rows/${encodeURIComponent(rowKey)}/unhide`,
        body,
      )
      .then((r) => r.data),

  // ─── Mutations (engine-scoped — backend issues 39 + 40) ───────────────────

  /** Logs what someone did or learned about this ENGINE that is not a
   *  verification — one engine event row. It wrote a shared engine
   *  investigation's blocked-on note until 2026-08-14 (backend ADR 0022),
   *  which is what reset every linked hotel's reminder clock; it moves no
   *  clock now. */
  logEngineNote: (engineKey: string, body: { note: string }) =>
    client
      .post(`${BASE}/engines/${encodeURIComponent(engineKey)}/note`, body)
      .then((r) => r.data),

  /** Records an attended session / outbound vendor message — the two
   *  verification kinds no automated store can reconstruct. */
  logEngineVerification: (
    engineKey: string,
    body: { kind: "attended-session" | "message-sent"; note: string },
  ) =>
    client
      .post(
        `${BASE}/engines/${encodeURIComponent(engineKey)}/verification`,
        body,
      )
      .then((r) => r.data),

  // ─── Mutations (fleet-wide — backend ADR 0039) ─────────────────────────────

  /**
   * Change the fleet-wide settings. Send only the values that CHANGE, plus the
   * mandatory reason: the server refuses (400) an unknown key, an out-of-bounds
   * value, a backstop shorter than twice the low-volume window, and a body
   * that changes nothing — each with its own sentence, which the toast shows.
   * One `tracking_admin_event` per call; the returned payload is the new
   * standing view. Takes effect on the next daily pass, not on save.
   */
  updateSettings: (
    body: UpdateTrackingSettingsBody,
  ): Promise<TrackingSettingsPayload> =>
    client.post(`${BASE}/settings`, body).then((r) => r.data),
};
